/**
 * Aktivitäten-Tab Startseite (Marcel 2026-10-08) – REINE Logik, EINE Stelle (App-Gegenstück:
 * core/helper/activity_stream_helper.dart). Backend: GET /api/activity/recent?limit&before.
 */

// 2026-10-08 (Abend, Marcel): + setup, dartRoom, follow
// 2026-10-12 (Marcel): + listingFollow (folgt Verein/Spielstätte/…), membership (Vereinsmitglied), ranking (neue Rangliste,
// Akteur = Listing), rankingLink (Profil mit Ranglisten-Platz verknüpft), newMember (neu bei DartCloud)
export type ActivityType = 'comment' | 'rating' | 'setupComment' | 'forumTopic' | 'forumPost' | 'post' | 'setup' | 'dartRoom' | 'follow'
  | 'listingFollow' | 'membership' | 'ranking' | 'rankingLink' | 'newMember' | 'onlineQuick';
export const ACTIVITY_TYPES: ActivityType[] = ['comment', 'rating', 'setupComment', 'forumTopic', 'forumPost', 'post', 'setup', 'dartRoom', 'follow',
  'listingFollow', 'membership', 'ranking', 'rankingLink', 'newMember', 'onlineQuick'];

export interface ActivityActor { id: string | null; name: string | null; image: string | null }
export interface ActivityTarget { kind: string | null; id: string | null; name: string | null; route: string | null }
export interface ActivityItem {
  id: string;
  type: ActivityType;
  createdAt: number;
  actor: ActivityActor;
  target: ActivityTarget;
  text: string | null;
  stars: number | null;
  reply: boolean;
}
export interface ActivityPage { items: ActivityItem[]; nextBefore: number | null }

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** Defensives Parsen eines Eintrags (unbekannter Typ / ohne id → null) */
export function parseActivityItem(raw: any): ActivityItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const id = str(raw.id);
  const type = ACTIVITY_TYPES.includes(raw.type) ? (raw.type as ActivityType) : null;
  if (!id || !type) return null;
  return {
    id,
    type,
    createdAt: num(raw.createdAt) ?? 0,
    actor: { id: str(raw.actor?.id), name: str(raw.actor?.name), image: str(raw.actor?.image) },
    target: { kind: str(raw.target?.kind), id: str(raw.target?.id), name: str(raw.target?.name), route: str(raw.target?.route) },
    text: str(raw.text),
    stars: num(raw.stars),
    reply: raw.reply === true,
  };
}

export function parseActivityPage(raw: any): ActivityPage {
  const items = Array.isArray(raw?.items) ? (raw.items.map(parseActivityItem).filter(Boolean) as ActivityItem[]) : [];
  return { items, nextBefore: num(raw?.nextBefore) };
}

/** i18n-Schlüssel der Satzschablone je Typ (Antworten eigene Form) */
export function activitySentenceKey(type: ActivityType, reply: boolean): string {
  const base = 'web.component.home-activity-list.';
  switch (type) {
    case 'comment': return base + (reply ? 'hat-auf-kommentar-geantwortet' : 'hat-kommentiert');
    case 'rating': return base + 'hat-bewertet';
    case 'setupComment': return base + (reply ? 'hat-im-setup-geantwortet' : 'hat-setup-kommentiert');
    case 'forumTopic': return base + 'hat-thema-erstellt';
    case 'forumPost': return base + 'hat-im-thema-geantwortet';
    case 'post': return base + 'hat-beitrag-veroeffentlicht';
    case 'setup': return base + 'hat-setup-angelegt';
    case 'dartRoom': return base + 'hat-dart-room-aktualisiert';
    case 'follow': return base + 'folgt-jetzt';
    case 'listingFollow': return base + 'folgt-jetzt';
    case 'membership': return base + 'ist-jetzt-mitglied';
    case 'ranking': return base + 'hat-rangliste-angelegt';
    case 'rankingLink': return base + 'steht-jetzt-in-rangliste';
    case 'newMember': return base + 'ist-jetzt-dabei';
    case 'onlineQuick': return base + 'sucht-online-gegner';
    default: return base + 'hat-kommentiert';
  }
}

/** Sterne als Text „★★★★☆“ (1–5), sonst leer */
export function starsText(stars: number | null): string {
  if (!stars || stars < 1) return '';
  const n = Math.min(5, Math.round(stars));
  return '★'.repeat(n) + '☆'.repeat(5 - n);
}

/** Relative Zeit (pure, injizierbares now) – Rückgabe als Schlüssel + Zahl für t() */
export function relativeTimeParts(createdAt: number, now = Date.now()): { key: string; value: number } {
  const diff = Math.max(0, now - createdAt);
  const min = Math.floor(diff / 60000);
  if (min < 1) return { key: 'gerade-eben', value: 0 };
  if (min < 60) return { key: min === 1 ? 'vor-1-minute' : 'vor-minuten', value: min };
  const h = Math.floor(min / 60);
  if (h < 24) return { key: h === 1 ? 'vor-1-stunde' : 'vor-stunden', value: h };
  const d = Math.floor(h / 24);
  if (d < 30) return { key: d === 1 ? 'vor-1-tag' : 'vor-tagen', value: d };
  const mo = Math.floor(d / 30);
  return { key: mo === 1 ? 'vor-1-monat' : 'vor-monaten', value: mo };
}

/** Seiten zusammenführen: Dubletten (id) raus, Reihenfolge bleibt */
export function mergeActivityPages(existing: ActivityItem[], next: ActivityItem[]): ActivityItem[] {
  const seen = new Set(existing.map((x) => x.id));
  return [...existing, ...next.filter((x) => !seen.has(x.id))];
}

/** Ziel-Route im Web: Online-Suchen führen in den Online-Bereich (Lobby mit „Mitspielen“), sonst die Backend-Route */
export const activityRoute = (item: ActivityItem): string | null => (item.type === 'onlineQuick' ? '/online' : item.target.route);

/** Anzeigename mit Fallback */
export const actorLabel = (a: ActivityActor, fallback: string): string => a.name ?? fallback;
export const targetLabel = (t: ActivityTarget, fallback: string): string => t.name ?? fallback;
